import { Spinner } from '@triptcip/common'
import { linearGradient } from 'polished'
import React from 'react'
import styled, { css, keyframes } from 'styled-components'

import {
  COLOR_BLUE,
  COLOR_ERROR,
  COLOR_GREEN,
  COLOR_ORANGE,
  GRADIENT_PRIMARY
} from '../theme'

const MIN_HEIGHT = 59
const MIN_WIDTH = 144

const keyframeAnimate = keyframes`
  0% {
    background-position: 0 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
`

const Inner = styled.div`
  position: absolute;
  top: 0;
  bottom: 5px;
  right: 0;
  left: 0;
  border-radius: ${props => props.$borderRadius};
  filter: blur(30px);
  z-index: -1;
  transition: background 120ms, opacity 120ms;
  will-change: background, opacity;
  ${linearGradient({
  colorStops: [
    COLOR_ORANGE,
    COLOR_ERROR,
    COLOR_BLUE,
    COLOR_GREEN
  ],
  toDirection: '-45deg'
})}

  opacity: ${props => props.$opacity};
  background-size: 600%;
  animation: ${keyframeAnimate} 16s linear infinite;
`

const IconBefore = styled.div`
  margin-right: .5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Component = styled.button`
  position: relative;
  border: 0;
  padding: 0;
  outline: 0;
  appearance: none;
  will-change: opacity, transform;
  transition: opacity 120ms ease, transform 120ms cubic-bezier(.2, 1, .3, 1);
  min-height: ${MIN_HEIGHT}px;
  color: rgba(255, 255, 255, .8);
  font-size: ${props => props.$fontSize};
  border-radius: ${props => props.$borderRadius};

  ${props => {
    if (props.$isWide) {
      return css`
          width: 100%;
        `
    }

    return css`
        width: ${props.$width}px;
      `
  }}

  ${props => {
    if (props.$fastAnimation) {
      return css`
          ${Inner} {
            animation-duration: 1000ms;
          }

          ${Content} {
            animation-duration: 1000ms;
          }
        `
    }
  }}

  ${props => {
    if (!props.$width) {
      return css`
          min-width: ${MIN_WIDTH}px;
        `
    }
  }}

  ${props => {
    if (props.disabled) {
      return css`
          opacity: .3;
          ${GRADIENT_PRIMARY};
        `
    } else {
      return css`
          &:active {
            transform: translateY(2px) !important;
          }

          &:hover,
          &:focus {
            transform: translateY(-1px);
          }
        `
    }
  }}
`

const IconAfter = styled.div`
  margin-left: .5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Children = styled.div`
  line-height: 1;
`

const Content = styled.div`
  padding: 1.2rem 1.6rem;
  border-radius: ${props => props.$borderRadius};
  font-weight: 600;
  z-index: 2;
  line-height: 1;
  transition: background 120ms, opacity 120ms, filter 120ms;
  will-change: background, opacity, filter;
  min-height: ${MIN_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    if (props.$isWide) {
      return css`
          width: 100%;
        `
    }

    return css`
        width: ${props.$width}px;
      `
  }}

  ${props => {
    if (!props.$width) {
      return css`
        min-width: ${MIN_WIDTH}px;
      `
    }
  }}

  ${linearGradient({
    colorStops: [
      COLOR_ORANGE,
      COLOR_ERROR,
      COLOR_BLUE,
      COLOR_GREEN
    ],
    toDirection: '-45deg'
  })}

  background-size: 600%;
  animation: ${keyframeAnimate} 16s linear infinite;
`

const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ButtonRemarkable = React.forwardRef(({
  children,
  onClick,
  disabled,
  isLoading = false,
  iconBefore,
  iconAfter,
  blurOpacity = 0.5,
  width,
  fontSize = '1rem',
  fastAnimation = false,
  type = 'button',
  isWide,
  borderRadius = '999px'
}, ref) => {
  const isDisabled = isLoading || disabled

  return (
    <Component
      ref={ref}
      disabled={isDisabled}
      onClick={onClick}
      $width={width}
      $fontSize={fontSize}
      $fastAnimation={fastAnimation}
      $isWide={isWide}
      $borderRadius={borderRadius}
      type={type}
    >
      {isLoading ? (
        <Loader>
          <Spinner variant={Spinner.VARIANT_LIGHT} />
        </Loader>
      ) : (
        <Content
          $width={width}
          $isWide={isWide}
          $disabled={isDisabled}
          $borderRadius={borderRadius}
        >
          {iconBefore && (
            <IconBefore>
              {iconBefore}
            </IconBefore>
          )}
          <Children>
            {children}
          </Children>
          {iconAfter && (
            <IconAfter>
              {iconAfter}
            </IconAfter>
          )}
        </Content>
      )}
      <Inner
        $disabled={isDisabled}
        $opacity={blurOpacity}
        $borderRadius={borderRadius}
      />
    </Component>
  )
})

export default ButtonRemarkable
